<template>
  <div class="table-row hover:bg-gray-50 dark:hover:bg-gray-800/50">
    <!-- Name Column -->
    <div class="table-cell py-2 px-2 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700">
      <span class="text-sm font-mono">{{ article.name || '-' }}</span>
    </div>

    <!-- External reference Column -->
    <div class="table-cell py-2 px-2 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700">
      <span class="text-sm font-mono">{{ article.external_reference || '-' }}</span>
    </div>

    <!-- Description Column -->
    <div class="table-cell py-2 px-2 align-middle border-r border-b border-gray-200 dark:border-gray-700 max-w-xs">
      <div class="flex items-center">
        <i class="pi pi-box text-gray-500 dark:text-gray-400 mr-2 flex-shrink-0" />
        <span>{{ article.description || '-' }}</span>
      </div>
    </div>

    <!-- Brand Column -->
    <div class="table-cell py-2 px-2 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700">
      <span class="text-sm text-gray-600 dark:text-gray-400">{{ article.brand_name || '-' }}</span>
    </div>

    <!-- Price Column -->
    <div class="table-cell py-2 px-2 text-right align-middle border-r border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
      <InputNumber
        v-if="isFixedType"
        v-model="localPrice"
        mode="currency"
        currency="EUR"
        locale="nl-NL"
        :min="0"
        :pt="{ pcInputText: { root: { class: 'text-right text-sm py-1', style: priceInputStyle } } }"
        :class="{ 'updating-input': isUpdating }"
        @input="handlePriceInput"
      />
      <span v-else class="text-yellow-600 dark:text-yellow-300 font-mono text-sm">
        {{ formatCurrency(article.sale_price) }}
      </span>
    </div>

    <!-- Unit Column -->
    <div class="table-cell py-2 px-1 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700">
      <span class="text-xs text-gray-500 dark:text-gray-400">{{ getMeasurementLabel(article.measurement) }}</span>
    </div>

    <!-- Quantity Column -->
    <div class="table-cell py-2 px-2 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
      <InputNumber
        v-model="localQuantity"
        :min="1"
        showButtons
        buttonLayout="stacked"
        class="qty-input"
        :class="{ 'updating-input': isUpdating }"
        :pt="{ pcInputText: { root: { style: qtyInputStyle } } }"
        @input="handleQuantityInput"
      />
    </div>

    <!-- Total Column -->
    <div class="table-cell py-2 px-2 text-right align-middle border-r border-b border-gray-200 dark:border-gray-700 max-w-32">
      <span
        ref="totalPriceRef"
        v-tooltip.top="isTotalTruncated ? { value: formatCurrency(totalPrice), showDelay: 0 } : undefined"
        class="text-green-600 dark:text-green-400 font-mono text-sm block truncate"
      >
        {{ formatCurrency(totalPrice) }}
      </span>
    </div>

    <!-- Type Column -->
    <div class="table-cell py-2 px-1 text-center align-middle border-r border-b border-gray-200 dark:border-gray-700">
      <Tag v-if="isFixedType" severity="info" class="text-xs">
        Offer
      </Tag>
    </div>

    <!-- Actions Column -->
    <div class="table-cell py-2 px-2 text-center align-middle border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-center gap-1">
        <i v-if="isUpdating" class="pi pi-spin pi-spinner text-blue-400 text-xs" />
        <button
          @click="handleRemove($event)"
          class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          title="Delete article"
        >
          <i class="pi pi-trash text-red-500 dark:text-red-400 text-sm" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";
import InputNumber from "primevue/inputnumber";
import Tag from "primevue/tag";
import { formatCurrency } from "src/utils/currency";
import { getMeasurementLabel } from "src/utils/article-utils";
import type { FetchArticleResponse, AddArticlesToTicketType } from "src/api/models";
import { AddArticlesToTicketType as ArticleType } from "src/api/models";

// Extend locally until the global type is fixed
interface ExtendedArticle extends FetchArticleResponse {
  quantity?: number;
  association_id?: string;
  type?: AddArticlesToTicketType;
  price?: number | null;
}

const props = defineProps<{
  article: ExtendedArticle;
  isUpdating?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update-quantity', associationId: string, quantity: number): void;
  (e: 'update-price', associationId: string, price: number): void;
  (e: 'remove', article: ExtendedArticle, event: Event): void;
}>();

// Local state for inline editing
const localQuantity = ref(props.article.quantity || 1);
const localPrice = ref(props.article.price ?? props.article.sale_price ?? 0);

// Ref for checking truncation
const totalPriceRef = ref<HTMLElement | null>(null);

// Computed properties
const isFixedType = computed(() => props.article.type === ArticleType.fixed);

const displayPrice = computed(() => {
  return props.article.price ?? props.article.sale_price ?? 0;
});

const totalPrice = computed(() => {
  const price = isFixedType.value ? displayPrice.value : (props.article.sale_price || 0);
  return price * (localQuantity.value || 1);
});

// Check if total price text is truncated
const isTotalTruncated = computed(() => {
  const el = totalPriceRef.value;
  if (!el) return false;
  return el.scrollWidth > el.clientWidth;
});

// Auto-size input widths based on content
const priceInputStyle = computed(() => {
  const formatted = formatCurrency(localPrice.value);
  const chars = Math.max(formatted.length, 8); // minimum 8 chars
  return `width: ${chars + 2}ch; min-width: 6rem;`;
});

const qtyInputStyle = computed(() => {
  const chars = String(localQuantity.value).length;
  const width = Math.max(chars + 1, 3); // minimum 3 chars
  return `width: ${width + 1}ch; min-width: 3rem; text-align: center;`;
});

// Sync local state when props change from external source
// Skip if the value is the same (avoid unnecessary updates)
watch(() => props.article.quantity, (newVal) => {
  if (newVal !== undefined && newVal !== localQuantity.value) {
    localQuantity.value = newVal;
  }
});

watch(() => props.article.price, (newVal) => {
  if (newVal !== undefined && newVal !== null && newVal !== localPrice.value) {
    localPrice.value = newVal;
  }
});

// Debounced quantity update - triggers 500ms after user stops changing
const emitQuantityUpdate = useDebounceFn((newQuantity: number) => {
  if (props.article.association_id && newQuantity !== props.article.quantity) {
    emit('update-quantity', props.article.association_id, newQuantity);
  }
}, 500);

// Debounced price update - triggers 500ms after user stops changing
const emitPriceUpdate = useDebounceFn((newPrice: number) => {
  const originalPrice = props.article.price ?? props.article.sale_price ?? 0;
  if (props.article.association_id && newPrice !== originalPrice) {
    emit('update-price', props.article.association_id, newPrice);
  }
}, 500);

// Handle quantity input event (fires on every keystroke)
function handleQuantityInput(event: { value: number | null }) {
  if (event.value !== null && event.value !== undefined && event.value >= 1) {
    localQuantity.value = event.value;
    emitQuantityUpdate(event.value);
  }
}

// Handle price input event (fires on every keystroke)
function handlePriceInput(event: { value: number | null }) {
  if (event.value !== null && event.value !== undefined) {
    localPrice.value = event.value;
    emitPriceUpdate(event.value);
  }
}

function handleRemove(event: Event) {
  emit('remove', props.article, event);
}
</script>

<style scoped>
.updating-input :deep(input) {
  @apply border-blue-400 dark:border-blue-500;
}

.qty-input :deep(input) {
  min-width: 2.5rem;
  width: auto;
  text-align: center;
  font-size: 0.875rem;
  padding: 0.25rem;
}

.qty-input :deep(.p-inputnumber-button) {
  width: 1.25rem !important;
  padding: 0 !important;
}

.qty-input :deep(.p-inputnumber-button-icon) {
  font-size: 0.65rem !important;
}
</style>
